/**
 * TreatX Coordinator Auth Context — SECURITY-S4 unified-surface migration.
 *
 * Reads ONLY from the canonical unified surface:
 *   localStorage.treatx_unified_token
 *   localStorage.treatx_unified_user
 *
 * The legacy role-scoped duplicate keys (`treatx_coordinator_token`,
 * `treatx_coordinator_user`) are no longer read or written.  Pre-existing
 * legacy entries are actively purged on mount.
 */
import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const CoordinatorAuthContext = createContext();
const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const UNIFIED_TOKEN_KEY = 'treatx_unified_token';
const UNIFIED_USER_KEY = 'treatx_unified_user';

const COORDINATOR_ROLES = new Set([
  'treatx_coordinator',
  'provider_coordinator',
  'treatx_super_admin',
  'treatx_admin',
]);

export const useCoordinatorAuth = () => {
  const context = useContext(CoordinatorAuthContext);
  if (!context) {
    throw new Error('useCoordinatorAuth must be used within CoordinatorAuthProvider');
  }
  return context;
};

const getInitialCoordinator = () => {
  // SECURITY-S4: purge legacy mirrors at module init.
  try {
    localStorage.removeItem('treatx_coordinator_token');
    localStorage.removeItem('treatx_coordinator_user');
  } catch { /* SSR / no-localStorage env */ }

  try {
    const raw = localStorage.getItem(UNIFIED_USER_KEY);
    if (!raw) return null;
    const u = JSON.parse(raw);
    if (!u || !COORDINATOR_ROLES.has(u.role)) return null;
    return {
      id: u.id,
      email: u.email,
      name: u.name,
      role: u.role,
      clinic_id: u.clinic_id ?? null,
    };
  } catch (e) {
    return null;
  }
};

export const CoordinatorAuthProvider = ({ children }) => {
  const [coordinator, setCoordinator] = useState(getInitialCoordinator);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Re-evaluate when localStorage is updated by UnifiedLoginPage.
    const sync = () => setCoordinator(getInitialCoordinator());
    window.addEventListener('storage', sync);
    return () => window.removeEventListener('storage', sync);
  }, []);

  const loginCoordinator = async (email, password) => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/auth/unified/login`, { email, password });

      if (data.success && data.user) {
        if (!COORDINATOR_ROLES.has(data.role)) {
          setLoading(false);
          return { success: false, error: 'This account is not a coordinator role.' };
        }
        // Canonical surface writes (no role-scoped mirrors).
        localStorage.setItem(UNIFIED_USER_KEY, JSON.stringify(data.user));

        const userData = {
          id: data.user.id,
          email: data.user.email,
          name: data.user.name,
          role: data.user.role || data.role,
          clinic_id: data.user.clinic_id,
        };
        setCoordinator(userData);
        setLoading(false);
        return { success: true, coordinator: userData };
      }

      setLoading(false);
      return { success: false, error: data.error || 'Login failed' };
    } catch (err) {
      setLoading(false);
      const msg = err.response?.data?.detail || err.response?.data?.error || 'Login failed';
      return { success: false, error: msg };
    }
  };

  const logoutCoordinator = () => {
    setCoordinator(null);
    localStorage.removeItem(UNIFIED_TOKEN_KEY);
    localStorage.removeItem(UNIFIED_USER_KEY);
    // Defence-in-depth: sweep any pre-existing legacy mirrors.
    localStorage.removeItem('treatx_coordinator_token');
    localStorage.removeItem('treatx_coordinator_user');
  };

  const isAuthenticated = !!coordinator;

  const value = {
    coordinator,
    loading,
    isAuthenticated,
    loginCoordinator,
    logoutCoordinator,
  };

  return (
    <CoordinatorAuthContext.Provider value={value}>
      {children}
    </CoordinatorAuthContext.Provider>
  );
};

export default CoordinatorAuthContext;
